import React from 'react';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QUERY_MAX_RETRIES } from 'constants/appConstants';
import { isRetryableError } from 'services/AxiosInstance';
import { ConfigProvider } from 'antd';

import App from './App';
import ErrorPage from './components/reusable/ErrorPage/ErrorPage';
import { NOTIFICATION_TIMEOUT } from './constants/Constant';
import './index.css';
import i18n from './locales/i18n';
import { store } from './redux/Store';
import reportWebVitals from './reportWebVitals';
import { ThemeData } from './types/GlobalCatalogueTypes';

// AntD token cascade requires raw values — CSS variables are read at runtime via getComputedStyle.
// Keep these in sync with tokens.css.
const cssVar = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const defaultData: ThemeData = {
  borderRadius: 4,
  colorPrimary: cssVar('--color-success'),
  controlOutline: cssVar('--color-success'),
  controlOutlineWidth: 1,
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Retry up to 3 times for transient errors (network, 408, 429, 5xx).
      // Non-retryable 4xx codes (400, 401, 402, 403, 404, 409, 410, 422)
      // are handled in the axios interceptor and terminate immediately.
      retry: (failureCount, error) =>
        isRetryableError(error) && failureCount < QUERY_MAX_RETRIES,
    },
  },
});
// Type declaration for the DevTools window property (compile-time only).
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import('@tanstack/query-core').QueryClient;
  }
}

// Expose QueryClient for the TanStack Query DevTools browser extension.
// The extension reads window.__TANSTACK_QUERY_CLIENT__ at runtime to inspect
// cached queries and their state. Guard ensures this never runs in production
// builds — yarn build sets NODE_ENV='production' and dead-code elimination
// removes the block entirely from the bundle.
if (process.env.NODE_ENV !== 'production') {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

root.render(
  // <React.StrictMode>
  <I18nextProvider i18n={i18n}>
    <Provider store={store}>
      <ErrorBoundary
        fallbackRender={({ error }) => <ErrorPage error={error} />}
      >
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <ToastContainer autoClose={NOTIFICATION_TIMEOUT} />
            <ConfigProvider
              theme={{
                token: defaultData,
                components: {
                  Form: {
                    verticalLabelPadding: '0 0 0.3125rem',
                  },
                },
              }}
            >
              <App />
            </ConfigProvider>
          </HelmetProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </Provider>
  </I18nextProvider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
